import { useState, useMemo } from "react";
import { canDo } from "@shared/roles";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import { useProspects, useCreateProspect, useUpdateProspect, useTransformProspect } from "@/hooks/use-prospects";
import { useMissions } from "@/hooks/use-missions";
import { useCommercials } from "@/hooks/use-profiles";
import { ActionsLiees } from "@/components/actions-liees";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AddressAutocomplete from "@/components/address-autocomplete";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchSelect } from "@/components/search-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/status-badge";
import { Plus, Search, UserPlus, Eye, Pencil, MoreHorizontal, ArrowRightLeft, ExternalLink } from "lucide-react";
import { EntiteIntervenants } from "@/components/ContactLiens";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Prospect, Profile, Mission } from "@shared/schema";

const statusLabels: Record<string, string> = {
  stand_by: "Stand-by",
  date_proposee: "Date proposée",
  demo_planifiee: "Démo planifiée",
  demo_realisee: "Démo réalisée",
  gagne: "Gagné",
  perdu: "Perdu",
};

const emptyForm = {
  societe: "",
  secteurActivite: "",
  groupe: "",
  civiliteContact: "" as string,
  prenomContact: "",
  nomContact: "",
  email: "",
  telephone: "",
  adresse: "",
  codePostal: "",
  ville: "",
  pays: "France",
  commercialId: "" as string,
  statut: "stand_by" as string,
  datePremierContact: "",
  notes: "",
};

export default function ProspectList() {
  const { profile } = useAuth();
  const { data: prospectsList, isLoading } = useProspects();
  const { data: missionsList } = useMissions();
  const { data: commercialsList } = useCommercials();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [transformOpen, setTransformOpen] = useState(false);
  const [revendeurFormOpen, setRevendeurFormOpen] = useState(false);
  const [revendeurFormData, setRevendeurFormData] = useState({
    companyName: "",
    contactFirstName: "",
    contactLastName: "",
    contactPhone: "",
    contactEmail: "",
    address: "",
    commercialId: "",
  });
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const { toast } = useToast();
  const createMutation = useCreateProspect();
  const updateMutation = useUpdateProspect();
  const transformMutation = useTransformProspect();

  const canEdit = canDo(profile?.accessRole) || profile?.role === 'planification' || profile?.role === 'commercial';
  const commercials = commercialsList || [];

  const prospects = (prospectsList || []).filter((p: Prospect) => p.actif !== false);

  const [formData, setFormData] = useState({ ...emptyForm, commercialId: profile?.id || "" });
  const [editData, setEditData] = useState({ ...emptyForm });

  const filteredProspects = prospects.filter((p: Prospect) => {
    const matchesSearch =
      p.societe.toLowerCase().includes(search.toLowerCase()) ||
      (p.nomContact && p.nomContact.toLowerCase().includes(search.toLowerCase())) ||
      (p.ville && p.ville.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === "all" || p.statut === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        ...formData,
        secteurActivite: formData.secteurActivite || null,
        groupe: formData.groupe || null,
        civiliteContact: (formData.civiliteContact as "M." | "Mme") || null,
        prenomContact: formData.prenomContact || null,
        nomContact: formData.nomContact || null,
        email: formData.email || null,
        telephone: formData.telephone || null,
        adresse: formData.adresse || null,
        codePostal: formData.codePostal || null,
        ville: formData.ville || null,
        pays: formData.pays || "France",
        commercialId: formData.commercialId || null,
        statut: formData.statut as any,
        datePremierContact: formData.datePremierContact || null,
        notes: formData.notes || null,
      } as any);
      setCreateOpen(false);
      toast({ title: "Prospect ajouté", description: "Le prospect a été créé avec succès" });
      setFormData({ ...emptyForm, commercialId: profile?.id || "" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de créer le prospect" });
    }
  };

  const openEdit = (prospect: Prospect) => {
    setSelectedProspect(prospect);
    setEditData({
      societe: prospect.societe,
      secteurActivite: prospect.secteurActivite || "",
      groupe: prospect.groupe || "",
      civiliteContact: prospect.civiliteContact || "",
      prenomContact: prospect.prenomContact || "",
      nomContact: prospect.nomContact || "",
      email: prospect.email || "",
      telephone: prospect.telephone || "",
      adresse: prospect.adresse || "",
      codePostal: prospect.codePostal || "",
      ville: prospect.ville || "",
      pays: prospect.pays || "France",
      commercialId: prospect.commercialId || "",
      statut: prospect.statut,
      datePremierContact: prospect.datePremierContact || "",
      notes: prospect.notes || "",
    });
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedProspect.id,
        ...editData,
        secteurActivite: editData.secteurActivite || null,
        groupe: editData.groupe || null,
        civiliteContact: (editData.civiliteContact as "M." | "Mme") || null,
        prenomContact: editData.prenomContact || null,
        nomContact: editData.nomContact || null,
        email: editData.email || null,
        telephone: editData.telephone || null,
        adresse: editData.adresse || null,
        codePostal: editData.codePostal || null,
        ville: editData.ville || null,
        pays: editData.pays || "France",
        commercialId: editData.commercialId || null,
        statut: editData.statut as any,
        datePremierContact: editData.datePremierContact || null,
        notes: editData.notes || null,
      } as any);
      setEditOpen(false);
      toast({ title: "Prospect modifié", description: "Les modifications ont été enregistrées" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier le prospect" });
    }
  };

  const openDetail = (prospect: Prospect) => {
    setSelectedProspect(prospect);
    setDetailOpen(true);
  };

  const handleTransform = async (type: "client" | "revendeur") => {
    if (!selectedProspect) return;
    try {
      await transformMutation.mutateAsync({ id: selectedProspect.id, type });
      setTransformOpen(false);
      setDetailOpen(false);
      toast({
        title: type === "client" ? "Client créé" : "Revendeur créé",
        description: `Le prospect "${selectedProspect.societe}" a été transformé en ${type === "client" ? "client" : "revendeur"} avec succès.`,
      });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message || "Impossible de transformer le prospect" });
    }
  };

  const openRevendeurForm = () => {
    if (!selectedProspect) return;
    setRevendeurFormData({
      companyName: selectedProspect.societe || "",
      contactFirstName: selectedProspect.prenomContact || "",
      contactLastName: selectedProspect.nomContact || "",
      contactPhone: selectedProspect.telephone || "",
      contactEmail: selectedProspect.email || "",
      address: [selectedProspect.adresse, selectedProspect.codePostal, selectedProspect.ville].filter(Boolean).join(", "),
      commercialId: selectedProspect.commercialId || "",
    });
    setTransformOpen(false);
    setRevendeurFormOpen(true);
  };

  const handleRevendeurSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedProspect.id,
        societe: revendeurFormData.companyName,
        prenomContact: revendeurFormData.contactFirstName || null,
        nomContact: revendeurFormData.contactLastName || null,
        telephone: revendeurFormData.contactPhone || null,
        email: revendeurFormData.contactEmail || null,
        commercialId: revendeurFormData.commercialId || null,
      } as any);
      await transformMutation.mutateAsync({ id: selectedProspect.id, type: "revendeur" });
      setRevendeurFormOpen(false);
      setDetailOpen(false);
      toast({ title: "Revendeur créé", description: `"${revendeurFormData.companyName}" a été transformé en revendeur avec succès.` });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Erreur", description: err.message || "Impossible de créer le revendeur" });
    }
  };

  const getCommercialName = (id: string | null) => {
    if (!id) return "—";
    const c = commercials.find((p: Profile) => p.id === id);
    return c ? `${c.firstName} ${c.lastName}` : "—";
  };

  const renderForm = (data: typeof formData, setData: typeof setFormData, onSubmit: (e: React.FormEvent) => void, isPending: boolean, submitLabel: string, showStatus = false) => (
    <form onSubmit={onSubmit} className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Société *</Label>
          <Input data-testid="input-societe" required value={data.societe} onChange={e => setData({ ...data, societe: e.target.value })} placeholder="Ex: Restaurant Le Gourmet" />
        </div>
        <div className="space-y-2">
          <Label>Secteur d'activité</Label>
          <Input data-testid="input-secteur" value={data.secteurActivite} onChange={e => setData({ ...data, secteurActivite: e.target.value })} placeholder="Restauration, Hôtellerie..." />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Groupe</Label>
          <Input data-testid="input-groupe" value={data.groupe} onChange={e => setData({ ...data, groupe: e.target.value })} placeholder="Ex: Intermarché, Boulangerie Ange" />
        </div>
        <div className="space-y-2">
          <Label>Date premier contact</Label>
          <Input data-testid="input-date-premier-contact" type="date" value={data.datePremierContact} onChange={e => setData({ ...data, datePremierContact: e.target.value })} />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium mb-3">Contact</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Civilité</Label>
            <Select value={data.civiliteContact} onValueChange={v => setData({ ...data, civiliteContact: v })}>
              <SelectTrigger data-testid="select-civilite"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="M.">M.</SelectItem>
                <SelectItem value="Mme">Mme</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Prénom</Label>
            <Input data-testid="input-prenom" value={data.prenomContact} onChange={e => setData({ ...data, prenomContact: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Nom</Label>
            <Input data-testid="input-nom" value={data.nomContact} onChange={e => setData({ ...data, nomContact: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input data-testid="input-email" type="email" value={data.email} onChange={e => setData({ ...data, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Téléphone</Label>
            <Input data-testid="input-telephone" value={data.telephone} onChange={e => setData({ ...data, telephone: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium mb-3">Adresse</h4>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>Adresse</Label>
            <AddressAutocomplete
              data-testid="input-adresse"
              value={data.adresse}
              onChange={(v) => setData({ ...data, adresse: v })}
              onSelect={(sel) => setData({ ...data, adresse: sel.voie, codePostal: sel.code_postal, ville: sel.ville })}
              placeholder="Saisir une adresse…"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Code postal</Label>
              <Input data-testid="input-code-postal" value={data.codePostal} onChange={e => setData({ ...data, codePostal: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Ville</Label>
              <Input data-testid="input-ville" value={data.ville} onChange={e => setData({ ...data, ville: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Pays</Label>
              <Input data-testid="input-pays" value={data.pays} onChange={e => setData({ ...data, pays: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Commercial</Label>
            <SearchSelect
              data-testid="select-commercial"
              value={data.commercialId || null}
              onValueChange={v => setData({ ...data, commercialId: v || "" })}
              options={commercials.map((c: Profile) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
              placeholder="Sélectionner un commercial..."
              emptyText="Aucun commercial trouvé"
            />
          </div>
          {showStatus && (
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={data.statut} onValueChange={v => setData({ ...data, statut: v })}>
                <SelectTrigger data-testid="select-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(statusLabels).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea data-testid="input-notes" value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} rows={3} />
      </div>

      <div className="flex justify-end pt-2">
        <Button data-testid="button-submit-prospect" type="submit" disabled={isPending}>
          {isPending ? "En cours..." : submitLabel}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="text-page-title">Prospects</h1>
          <p className="text-muted-foreground mt-1">Gestion des prospects et suivi des démonstrations</p>
        </div>
        {canEdit && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-prospect"><Plus className="w-4 h-4 mr-2" />Ajouter un prospect</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nouveau prospect</DialogTitle>
                <DialogDescription>Créer une nouvelle fiche prospect</DialogDescription>
              </DialogHeader>
              {renderForm(formData, setFormData, handleSubmit, createMutation.isPending, "Créer")}
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="input-search"
            placeholder="Rechercher par société, contact, ville..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="select-status-filter" className="w-[200px]">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statusLabels).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Société</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Commercial</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Premier contact</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProspects?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun prospect trouvé</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredProspects?.map((prospect: Prospect) => (
                  <TableRow key={prospect.id} data-testid={`row-prospect-${prospect.id}`}>
                    <TableCell className="font-medium" data-testid={`text-societe-${prospect.id}`}>{prospect.societe}</TableCell>
                    <TableCell data-testid={`text-contact-${prospect.id}`}>
                      {[prospect.civiliteContact, prospect.prenomContact, prospect.nomContact].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell data-testid={`text-ville-${prospect.id}`}>{prospect.ville || "—"}</TableCell>
                    <TableCell data-testid={`text-commercial-${prospect.id}`}>{getCommercialName(prospect.commercialId)}</TableCell>
                    <TableCell>
                      <StatusBadge status={prospect.statut} type="prospect" label={statusLabels[prospect.statut] || prospect.statut} />
                    </TableCell>
                    <TableCell data-testid={`text-date-${prospect.id}`}>
                      {prospect.datePremierContact
                        ? format(new Date(prospect.datePremierContact), "dd/MM/yyyy")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`button-menu-${prospect.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetail(prospect)} data-testid={`menu-view-${prospect.id}`}>
                            <Eye className="h-4 w-4 mr-2" />Voir la fiche
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => openEdit(prospect)} data-testid={`menu-edit-${prospect.id}`}>
                              <Pencil className="h-4 w-4 mr-2" />Modifier
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier le prospect</DialogTitle>
            <DialogDescription>Modifier la fiche prospect</DialogDescription>
          </DialogHeader>
          {renderForm(editData, setEditData, handleEdit, updateMutation.isPending, "Enregistrer", true)}
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="text-detail-title">{selectedProspect?.societe}</DialogTitle>
            <DialogDescription>Fiche prospect</DialogDescription>
          </DialogHeader>
          {selectedProspect && (
            <div className="space-y-6 pt-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center gap-3">
                <StatusBadge status={selectedProspect.statut} type="prospect" label={statusLabels[selectedProspect.statut]} />
                {selectedProspect.groupe && (
                  <span className="text-sm text-muted-foreground">Groupe: {selectedProspect.groupe}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Informations</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Société:</span> {selectedProspect.societe}</div>
                    {selectedProspect.secteurActivite && <div><span className="text-muted-foreground">Secteur:</span> {selectedProspect.secteurActivite}</div>}
                    {selectedProspect.groupe && <div><span className="text-muted-foreground">Groupe:</span> {selectedProspect.groupe}</div>}
                    {selectedProspect.datePremierContact && (
                      <div><span className="text-muted-foreground">Premier contact:</span> {format(new Date(selectedProspect.datePremierContact), "dd MMMM yyyy", { locale: fr })}</div>
                    )}
                    <div><span className="text-muted-foreground">Commercial:</span> {getCommercialName(selectedProspect.commercialId)}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contact</h4>
                  <div className="space-y-2 text-sm">
                    <div>{[selectedProspect.civiliteContact, selectedProspect.prenomContact, selectedProspect.nomContact].filter(Boolean).join(" ") || "—"}</div>
                    {selectedProspect.email && <div>{selectedProspect.email}</div>}
                    {selectedProspect.telephone && <div>{selectedProspect.telephone}</div>}
                  </div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mt-4">Adresse</h4>
                  <div className="space-y-1 text-sm">
                    {selectedProspect.adresse && <div>{selectedProspect.adresse}</div>}
                    <div>{[selectedProspect.codePostal, selectedProspect.ville].filter(Boolean).join(" ")}</div>
                    {selectedProspect.pays && selectedProspect.pays !== "France" && <div>{selectedProspect.pays}</div>}
                  </div>
                </div>
              </div>

              {selectedProspect.notes && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Notes</h4>
                  <p className="text-sm bg-muted p-3 rounded-md whitespace-pre-wrap" onCopy={handlePlainTextCopy}>{selectedProspect.notes}</p>
                </div>
              )}

              {(() => {
                const prospectMissions = (missionsList || []).filter((m: any) => m.prospectId === selectedProspect.id);
                if (prospectMissions.length === 0) return null;
                return (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Missions liées</h4>
                    <div className="space-y-2">
                      {prospectMissions.map((m: any) => (
                        <div key={m.id} className="flex items-center justify-between bg-muted rounded-md px-3 py-2 text-sm">
                          <div>
                            <span className="font-medium">{m.eventName || m.description?.substring(0, 50) || "Mission"}</span>
                            <span className="text-muted-foreground ml-2">
                              {format(new Date(m.startDate), "dd/MM/yyyy")}
                            </span>
                          </div>
                          <StatusBadge status={m.status} type="mission" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <ActionsLiees emetteurType="prospect" emetteurId={selectedProspect.id} />
              <EntiteIntervenants entiteType="prospect" entiteId={selectedProspect.id} />

              {selectedProspect.transformeEnClientId && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-400">
                    <ArrowRightLeft className="h-4 w-4" />
                    <span className="text-sm font-medium">Transformé en client</span>
                    <a href="/clients" className="ml-auto text-sm underline flex items-center gap-1" data-testid="link-transformed-client">
                      Voir la fiche client <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {selectedProspect.transformeEnRevendeurId && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-400">
                    <ArrowRightLeft className="h-4 w-4" />
                    <span className="text-sm font-medium">Transformé en revendeur</span>
                    <a href="/resellers" className="ml-auto text-sm underline flex items-center gap-1" data-testid="link-transformed-reseller">
                      Voir la fiche revendeur <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              )}

              {selectedProspect.statut === "gagne" && !selectedProspect.transformeEnClientId && !selectedProspect.transformeEnRevendeurId && canEdit && (
                <div className="border-t pt-4">
                  <Dialog open={transformOpen} onOpenChange={setTransformOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="button-transform" className="w-full" variant="default">
                        <ArrowRightLeft className="h-4 w-4 mr-2" />
                        Transformer en client ou revendeur
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Transformer le prospect</DialogTitle>
                        <DialogDescription>
                          Choisissez le type d'entité à créer à partir de ce prospect.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col gap-3 pt-4">
                        <Button
                          data-testid="button-transform-client"
                          onClick={() => handleTransform("client")}
                          disabled={transformMutation.isPending}
                        >
                          Transformer en Client
                        </Button>
                        <Button
                          data-testid="button-transform-reseller"
                          variant="outline"
                          onClick={openRevendeurForm}
                          disabled={transformMutation.isPending}
                        >
                          Transformer en Revendeur
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {canEdit && !selectedProspect.transformeEnClientId && !selectedProspect.transformeEnRevendeurId && (
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => { setDetailOpen(false); openEdit(selectedProspect); }} data-testid="button-edit-from-detail">
                    <Pencil className="h-4 w-4 mr-2" />Modifier
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={revendeurFormOpen} onOpenChange={setRevendeurFormOpen}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Créer le revendeur</DialogTitle>
            <DialogDescription>
              Vérifiez et complétez les informations pré-remplies avant de créer le revendeur.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRevendeurSubmit} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Nom de la société *</Label>
              <Input
                required
                value={revendeurFormData.companyName}
                onChange={e => setRevendeurFormData(d => ({ ...d, companyName: e.target.value }))}
                placeholder="Nom de la société"
                data-testid="input-revendeur-company"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom contact</Label>
                <Input
                  value={revendeurFormData.contactFirstName}
                  onChange={e => setRevendeurFormData(d => ({ ...d, contactFirstName: e.target.value }))}
                  placeholder="Prénom"
                  data-testid="input-revendeur-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom contact</Label>
                <Input
                  value={revendeurFormData.contactLastName}
                  onChange={e => setRevendeurFormData(d => ({ ...d, contactLastName: e.target.value }))}
                  placeholder="Nom"
                  data-testid="input-revendeur-lastname"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={revendeurFormData.contactPhone}
                  onChange={e => setRevendeurFormData(d => ({ ...d, contactPhone: e.target.value }))}
                  placeholder="06 xx xx xx xx"
                  data-testid="input-revendeur-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={revendeurFormData.contactEmail}
                  onChange={e => setRevendeurFormData(d => ({ ...d, contactEmail: e.target.value }))}
                  placeholder="email@exemple.fr"
                  data-testid="input-revendeur-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={revendeurFormData.address}
                onChange={e => setRevendeurFormData(d => ({ ...d, address: e.target.value }))}
                placeholder="Adresse complète"
                data-testid="input-revendeur-address"
              />
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRevendeurFormOpen(false)}>Annuler</Button>
              <Button
                type="submit"
                disabled={transformMutation.isPending || updateMutation.isPending}
                data-testid="button-confirm-create-revendeur"
              >
                {(transformMutation.isPending || updateMutation.isPending) ? "Création..." : "Créer le revendeur"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
